import React from 'react';
import { FormGroup, InputLabel, Tooltip } from '@material-ui/core';
import classNames from 'clsx';
import { useSelector } from 'react-redux';

import LabelWithBrackets from 'app/components/LabelWithBrackets/LabelWithBrackets';
import { SelectBox, SelectBoxMode } from 'app/components/SelectBox';
import { FiltersEnums, InsuranceEnum } from 'app/shared/enums';
import { getPreparedOffersArray } from 'app/store/data/quotationResult';

import {
  SelectBoxGroupControlProps,
  SelectBoxGroupItemValue,
  SelectBoxOptions,
} from './SelectBoxGroup.interface';
import { useStyles } from './SelectBoxGroup.styles';

const SelectBoxGroupControl = ({
  options,
  label,
  input,
  input: { value },
  columnDirection = false,
  isOptionDisabled,
  numberOffers,
  sendGTMEvent,
}: SelectBoxGroupControlProps) => {
  const classes = useStyles();

  const offers = useSelector(getPreparedOffersArray);
  const isAcAvailable = offers.some((offer) => offer.ac);

  const handleSelectBoxChange = (
    checked: boolean,
    option: SelectBoxOptions
  ) => {
    const { value } = option;

    sendGTMEvent && sendGTMEvent(option, checked);

    let newValue: Array<SelectBoxGroupItemValue> = [];

    if (Array.isArray(input.value)) {
      newValue = [...input.value];
    }

    if (checked) {
      newValue.push(value);
    } else {
      newValue.splice(newValue.indexOf(value), 1);
    }

    input.onChange(newValue);
  };

  return (
    <FormGroup role="group" aria-labelledby={`${input.name}-label`}>
      <InputLabel className={classes.label} id={`${input.name}-label`}>
        {label}
      </InputLabel>
      <div className={classNames({ [classes.column]: columnDirection })}>
        {options?.map((option) => {
          const isDisabled = isOptionDisabled && isOptionDisabled(option.value);
          const countOffers = numberOffers && numberOffers(option.value);

          const isAcFilterDisable =
            option.title === InsuranceEnum.Ac && !isAcAvailable;

          const isChecked =
            Array.isArray(value) && value.includes(option.value);

          const title =
            countOffers !== undefined ? (
              <LabelWithBrackets
                label={option.title}
                textInBrackets={countOffers}
              />
            ) : (
              option.title
            );

          return (
            <div className={classes.wrapper} key={option.title + option.value}>
              {isDisabled || isAcFilterDisable ? (
                <Tooltip
                  title={
                    isAcFilterDisable
                      ? FiltersEnums.AcFilterNoOffers
                      : isChecked
                      ? FiltersEnums.MoreFilterOptionsNoOffer
                      : FiltersEnums.MoreFilterOptionsDisable
                  }
                  placement={'top'}
                  arrow
                  classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
                  key={option.title + option.value}
                >
                  <span>
                    {
                      <SelectBox
                        readonly={
                          (isDisabled && !isChecked) || isAcFilterDisable
                        }
                        className={classNames(classes.item, {
                          [classes.selectItemMoreFilters]: columnDirection,
                        })}
                        classes={{
                          root: classes.selectBoxRoot,
                        }}
                        title={title}
                        mode={SelectBoxMode.Icon2}
                        checked={isChecked}
                        onChange={(checked: boolean) =>
                          handleSelectBoxChange(checked, option)
                        }
                      />
                    }
                  </span>
                </Tooltip>
              ) : (
                <SelectBox
                  tabIndex={0}
                  readonly={(isDisabled && !isChecked) || isAcFilterDisable}
                  key={option.title + option.value}
                  className={classNames(classes.item, {
                    [classes.selectItemMoreFilters]: columnDirection,
                  })}
                  classes={{
                    root: classes.selectBoxRoot,
                  }}
                  title={title}
                  mode={SelectBoxMode.Icon2}
                  checked={isChecked}
                  onChange={(checked: boolean) =>
                    handleSelectBoxChange(checked, option)
                  }
                />
              )}
            </div>
          );
        })}
      </div>
    </FormGroup>
  );
};

export default SelectBoxGroupControl;
